/**
 * InstagramLancioVetrina — post Instagram 1080×1350 per annunciare il
 * proprio sito personale appena attivato.
 *
 * iter v32 r37w · User M1456 — 4 LAYOUT a scelta.
 * - layout=1 → BRUTALIST DARK (nome gigante, sfondo nero, accent brand color)
 * - layout=2 → EDITORIAL PHOTO (foto full-bleed sopra, info sotto)
 * - layout=3 → MINIMAL CREAM (sfondo chiaro serif, foto a destra)
 * - layout=4 → MAGAZINE SPLIT (split 50/50 foto·testo, cornice)
 *
 * Tutti i 4 layout supportano:
 * - foto reale agente (`photo_url`) → fallback iniziali se mancante
 * - nome agenzia (`agency_name`) + logo agenzia (`agency_logo_url`) in B/N
 *   automatico tramite endpoint `/api/public/agency/{id}/logo?bw=1&color=...`
 * - colore brand (`color`) come accent
 * - nome agente che si auto-ridimensiona per non sforare il canvas
 * - NESSUNA menzione di "Pronto" — è un post personale dell'agente
 *
 * Routing: /share/lancio-vetrina?layout=1&slug=...&display_name=...&color=#A8C97F
 *          &city=...&bio=...&years=...&properties=...&services=...
 *          &photo_url=...&agency_name=...&agency_logo_url=...
 *
 * Esportabile come PNG via Playwright screenshot di [data-testid="ig-post-canvas"]
 * a viewport 1080×1350.
 */
import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { exportStaticAsPng } from "@/lib/staticExport";

const DEFAULTS = {
  slug: "il-tuo-nome",
  display_name: "Luca Ferrari",
  color: "#A8C97F",
  city: "Bergamo",
  bio: "Agente immobiliare. Tutti i miei immobili sempre aggiornati, in un solo link.",
  years: "",
};

const sanitize = (s, max = 200) =>
  String(s || "").trim().slice(0, max);

const hexColor = (s, fallback = "#A8C97F") => {
  const v = String(s || "").trim();
  if (/^#?[0-9a-fA-F]{6}$/.test(v)) {
    return v.startsWith("#") ? v.toUpperCase() : `#${v.toUpperCase()}`;
  }
  return fallback;
};

/** Restituisce le iniziali per il placeholder foto. */
function initialsOf(name) {
  if (!name) return "·";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "·";
  return ((parts[0][0] || "") + (parts[parts.length - 1][0] || "")).toUpperCase();
}

/**
 * Trasforma l'URL del logo agenzia in versione B/N pre-elaborata dal
 * backend (`?bw=1&color=black|white`). Per URL esterne (https://…)
 * ritorna l'URL invariato.
 */
function logoBwUrl(url, color = "black") {
  if (!url) return null;
  if (url.includes("/api/public/agency/") && url.includes("/logo")) {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}bw=1&w=2048&color=${color}`;
  }
  return url;
}

/** Hook per caricare l'immagine in modo silenzioso (fallback se 404). */
function useImageOk(src) {
  const [ok, setOk] = React.useState(!!src);
  React.useEffect(() => {
    if (!src) { setOk(false); return; }
    let alive = true;
    const img = new Image();
    img.onload = () => { if (alive) setOk(true); };
    img.onerror = () => { if (alive) setOk(false); };
    img.src = src;
    return () => { alive = false; };
  }, [src]);
  return ok;
}

export default function InstagramLancioVetrina() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  // iter v32 r37x · User M1457 — Nomi layout allineati al vocabolario
  // social (`tokens.js`): Moderno / Lussuoso / Minimal / Elegante.
  // Accetto sia stringhe (moderno|lussuoso|minimal|elegante) che gli alias
  // numerici legacy (1|2|3|4) per backward compat con URL già condivisi.
  const LAYOUT_OPTIONS = [
    { key: "moderno", label: "Moderno", alias: "1" },
    { key: "lussuoso", label: "Lussuoso", alias: "2" },
    { key: "minimal", label: "Minimal", alias: "3" },
    { key: "elegante", label: "Elegante", alias: "4" },
  ];
  const rawLayout = String(params.get("layout") || "moderno").toLowerCase();
  const layoutKey =
    LAYOUT_OPTIONS.find((o) => o.key === rawLayout || o.alias === rawLayout)?.key
    || "moderno";

  const slug = sanitize(params.get("slug") || DEFAULTS.slug, 40);
  const displayName = sanitize(params.get("display_name") || DEFAULTS.display_name, 50);
  const color = hexColor(params.get("color"));
  const city = sanitize(params.get("city") || DEFAULTS.city, 30);
  const bio = sanitize(params.get("bio") || DEFAULTS.bio, 160);
  const years = sanitize(params.get("years") || DEFAULTS.years, 4);
  const properties = sanitize(params.get("properties") || "07", 4);
  const services = sanitize(params.get("services") || "VENDITA · AFFITTO · STIMA", 50);
  const photoUrl = params.get("photo_url") || "";
  const agencyName = sanitize(params.get("agency_name") || "", 40);
  const agencyLogoUrl = params.get("agency_logo_url") || "";

  const data = {
    slug, displayName, color, city, bio, years, properties, services,
    photoUrl, agencyName, agencyLogoUrl,
  };

  const setLayout = (l) => {
    const p = new URLSearchParams(params);
    p.set("layout", String(l));
    setParams(p);
  };

  const [downloading, setDownloading] = React.useState(false);
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const node = document.querySelector('[data-testid="ig-post-canvas"]');
      if (!node) throw new Error("Canvas non trovato");
      const safeSlug = slug.replace(/[^a-z0-9-]+/gi, "-").toLowerCase() || "agente";
      const filename = `pronto-lancio-${safeSlug}-${layoutKey}.png`;
      await exportStaticAsPng({ node, targetWidth: 1080, filename });
    } catch (e) {
      alert(`Errore durante l'esportazione: ${e?.message || e}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#1C1C1A] py-10"
      style={{ fontFamily: "'Inter','Helvetica Neue',sans-serif" }}
    >
      {/* Selettore layout + Download — non incluso nello screenshot */}
      <div className="mx-auto mb-6 flex flex-wrap items-center justify-center gap-2 px-4" data-testid="layout-selector">
        {LAYOUT_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setLayout(opt.key)}
            data-testid={`layout-btn-${opt.key}`}
            className={`text-xs px-4 py-2 rounded-full font-semibold transition-all border ${
              layoutKey === opt.key
                ? "bg-white text-[#1C1C1A] border-white shadow-lg"
                : "bg-transparent text-white/70 border-white/15 hover:border-white/40 hover:text-white"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="mx-auto mb-8 flex items-center justify-center gap-3 px-4">
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          data-testid="download-png-btn"
          className="text-sm px-6 py-2.5 rounded-full font-bold bg-[#A8C97F] text-[#1C1C1A] hover:bg-[#B8D98F] disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-lg"
        >
          {downloading ? "Genero PNG…" : "Scarica PNG 1080×1350"}
        </button>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-xs px-4 py-2 rounded-full font-semibold text-white/50 hover:text-white/90"
          data-testid="layout-back"
        >
          ← Torna indietro
        </button>
      </div>

      {/* Canvas Instagram 1080×1350 */}
      <div className="flex items-center justify-center">
        {layoutKey === "lussuoso" && <LayoutEditorial {...data} />}
        {layoutKey === "minimal" && <LayoutMinimal {...data} />}
        {layoutKey === "elegante" && <LayoutSplit {...data} />}
        {layoutKey === "moderno" && <LayoutBrutalist {...data} />}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Wrapper canvas — dimensioni fisse 1080×1350 con shadow + testid
 * ────────────────────────────────────────────────────────────────────── */
function Canvas({ children, bg = "#0A0A0A", textColor = "#FAF6EE" }) {
  return (
    <div
      data-testid="ig-post-canvas"
      className="relative overflow-hidden shadow-[0_40px_120px_rgba(0,0,0,0.65)]"
      style={{
        width: 1080,
        height: 1350,
        background: bg,
        color: textColor,
        fontFamily: "'Inter','Helvetica Neue',sans-serif",
      }}
    >
      {children}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Foto agente — se `photoUrl` è valido lo mostra, sennò iniziali grandi
 * ────────────────────────────────────────────────────────────────────── */
function AgentPhoto({ photoUrl, displayName, color, size = 240, radius = 4, accentBorder = true }) {
  const ok = useImageOk(photoUrl);
  return (
    <div
      style={{
        width: size,
        height: size * 1.2,
        flexShrink: 0,
        borderRadius: radius,
        overflow: "hidden",
        background: "#1F1F1E",
        border: accentBorder ? `1px solid ${color}33` : "none",
        position: "relative",
      }}
    >
      {ok && photoUrl ? (
        <img
          src={photoUrl}
          alt=""
          crossOrigin="anonymous"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "'Archivo','Inter',sans-serif",
            fontWeight: 900,
            fontSize: size * 0.5,
            color: color,
            letterSpacing: "-0.02em",
          }}
        >
          {initialsOf(displayName)}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Logo agenzia in B/N (bianco o nero) — fallback testo nome agenzia
 * ────────────────────────────────────────────────────────────────────── */
function AgencyMark({ agencyName, agencyLogoUrl, theme = "dark", size = 36 }) {
  const isDark = theme === "dark";
  const fg = isDark ? "#FAF6EE" : "#1C1C1A";
  const muted = isDark ? "rgba(250,246,238,0.55)" : "rgba(28,28,26,0.55)";
  const url = logoBwUrl(agencyLogoUrl, isDark ? "white" : "black");
  const ok = useImageOk(url);
  if (!agencyName && !agencyLogoUrl) return null;
  return (
    <div className="flex items-center gap-3">
      {ok && url ? (
        <img
          src={url}
          alt={agencyName || ""}
          crossOrigin="anonymous"
          style={{
            maxHeight: size,
            maxWidth: size * 3,
            objectFit: "contain",
            display: "block",
          }}
        />
      ) : (
        agencyName && (
          <div
            style={{
              fontSize: 13,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              fontWeight: 700,
              color: fg,
            }}
          >
            {agencyName}
          </div>
        )
      )}
      {ok && url && agencyName && (
        <div
          style={{
            fontSize: 11,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            fontWeight: 600,
            color: muted,
            borderLeft: `1px solid ${muted}`,
            paddingLeft: 12,
          }}
        >
          {agencyName}
        </div>
      )}
    </div>
  );
}

/* Auto-sizing helper per il nome agente sul layout 1 / 4 (Archivo Black). */
function heroFontSizeFor(name) {
  const parts = name.split(/\s+/);
  const maxLen = Math.max(parts[0].length, parts.slice(1).join(" ").length || 0);
  if (maxLen > 12) return 110;
  if (maxLen > 10) return 132;
  if (maxLen > 8) return 156;
  if (maxLen > 6) return 178;
  return 200;
}

/* ══════════════════════════════════════════════════════════════════════
 * LAYOUT 1 — BRUTALIST DARK
 * ══════════════════════════════════════════════════════════════════════ */
function LayoutBrutalist({ slug, displayName, color, city, bio, years, properties, services, photoUrl, agencyName, agencyLogoUrl }) {
  const [firstName, ...rest] = displayName.split(/\s+/);
  const lastName = rest.join(" ");
  const heroFontSize = heroFontSizeFor(displayName);

  return (
    <Canvas bg="#0A0A0A" textColor="#FAF6EE">
      {/* grid pattern */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.06,
          backgroundImage:
            "linear-gradient(to right, #FAF6EE 1px, transparent 1px), linear-gradient(to bottom, #FAF6EE 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />
      <div aria-hidden className="absolute" style={{ top: 0, left: 0, bottom: 0, width: 8, background: color }} />

      {/* Top eyebrow + Online pill */}
      <div className="absolute top-[64px] left-[80px] right-[80px] flex items-start justify-between">
        <div>
          <div style={{ fontSize: 13, letterSpacing: "0.34em", textTransform: "uppercase", fontWeight: 700, color, lineHeight: 1.1 }}>
            Annuncio Personale
          </div>
          <div style={{ fontSize: 12, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 500, color: "rgba(250,246,238,0.55)", marginTop: 6 }}>
            Anno {new Date().getFullYear()}
          </div>
        </div>
        <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full" style={{ background: "rgba(250,246,238,0.08)", border: "1px solid rgba(250,246,238,0.18)", color: "#FAF6EE", fontSize: 12, letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 600 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, boxShadow: `0 0 0 4px ${color}26` }} />
          Online
        </div>
      </div>

      {/* HERO NAME */}
      <div className="absolute" style={{ top: 200, left: 80, right: 80 }}>
        <div style={{ fontFamily: "'Archivo','Inter',sans-serif", fontWeight: 900, fontSize: heroFontSize, lineHeight: 0.86, letterSpacing: "-0.045em", color: "#FAF6EE", textTransform: "uppercase" }}>
          {firstName}
          {lastName && (
            <>
              <br />
              <span style={{ color }}>{lastName}.</span>
            </>
          )}
        </div>
        <div style={{ marginTop: 28, display: "flex", alignItems: "center", gap: 14, fontSize: 22, letterSpacing: "0.04em", fontWeight: 500, color: "rgba(250,246,238,0.78)" }}>
          <span style={{ textTransform: "uppercase", letterSpacing: "0.24em", fontSize: 14, fontWeight: 700, color }}>Agente Immobiliare</span>
          <span style={{ opacity: 0.4 }}>·</span>
          <span style={{ textTransform: "uppercase", letterSpacing: "0.24em", fontSize: 14, fontWeight: 500 }}>{city}</span>
          {years && (
            <>
              <span style={{ opacity: 0.4 }}>·</span>
              <span style={{ textTransform: "uppercase", letterSpacing: "0.24em", fontSize: 14, fontWeight: 500 }}>{years} anni</span>
            </>
          )}
        </div>
      </div>

      {/* Photo + Bio */}
      <div className="absolute" style={{ left: 80, right: 80, top: 640, display: "flex", gap: 28, alignItems: "flex-start" }}>
        <AgentPhoto photoUrl={photoUrl} displayName={displayName} color={color} size={200} radius={4} />
        <div style={{ flex: 1, paddingTop: 4 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, color, marginBottom: 14 }}>
            ─ Chi sono
          </div>
          <div style={{ fontSize: 26, lineHeight: 1.36, fontWeight: 400, color: "rgba(250,246,238,0.92)", letterSpacing: "-0.01em" }}>
            {bio}
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="absolute" style={{ left: 80, right: 80, top: 920, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>
        {[
          { value: properties, label: "Immobili in vetrina" },
          { value: years || "—", label: years ? "Anni di esperienza" : "Esperienza" },
          { value: city.toUpperCase(), label: "Zona di attività" },
        ].map((s, i) => (
          <div key={i} style={{ background: "rgba(250,246,238,0.04)", border: "1px solid rgba(250,246,238,0.10)", borderRadius: 6, padding: "20px 22px", position: "relative" }}>
            <div style={{ fontFamily: "'Archivo','Inter',sans-serif", fontWeight: 900, fontSize: s.value && String(s.value).length > 6 ? 32 : 56, lineHeight: 0.95, letterSpacing: "-0.02em", color, marginBottom: 8 }}>
              {s.value}
            </div>
            <div style={{ fontSize: 10.5, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 600, color: "rgba(250,246,238,0.65)" }}>
              {s.label}
            </div>
            <div style={{ position: "absolute", top: 12, right: 14, fontSize: 9, letterSpacing: "0.18em", fontWeight: 700, color: "rgba(250,246,238,0.35)" }}>
              · {String(i + 1).padStart(2, "0")}
            </div>
          </div>
        ))}
      </div>

      {/* Divider */}
      <div className="absolute" style={{ left: 80, right: 80, top: 1100, height: 1, background: "rgba(250,246,238,0.15)" }} />

      {/* URL */}
      <div className="absolute" style={{ left: 80, right: 80, top: 1130 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, color: "rgba(250,246,238,0.55)", marginBottom: 12 }}>
          Trovami qui
        </div>
        <div style={{ fontFamily: "'JetBrains Mono','SF Mono','Menlo',monospace", fontWeight: 700, fontSize: 36, color: "#FAF6EE", letterSpacing: "-0.015em", lineHeight: 1.05, wordBreak: "break-word" }}>
          <span style={{ color: "rgba(250,246,238,0.5)" }}>prontoimmo.it/agente/</span>
          <span style={{ color }}>{slug}</span>
        </div>
      </div>

      {/* Bottom: agency mark + servizi + arrow */}
      <div className="absolute" style={{ bottom: 56, left: 80, right: 80, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {(agencyName || agencyLogoUrl) && (
            <AgencyMark agencyName={agencyName} agencyLogoUrl={agencyLogoUrl} theme="dark" size={32} />
          )}
          <div style={{ fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 700, color: "rgba(250,246,238,0.55)", lineHeight: 1.4 }}>
            <div style={{ color, fontSize: 10, letterSpacing: "0.32em", marginBottom: 6 }}>─ Servizi</div>
            {services}
          </div>
        </div>
        <div style={{ width: 88, height: 88, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 16px 36px ${color}55`, flexShrink: 0 }}>
          <svg width="38" height="38" viewBox="0 0 36 36" fill="none">
            <path d="M6 18 H 28 M 20 10 L 28 18 L 20 26" stroke="#0A0A0A" strokeWidth="3" strokeLinecap="square" strokeLinejoin="miter" />
          </svg>
        </div>
      </div>
    </Canvas>
  );
}

/* ══════════════════════════════════════════════════════════════════════
 * LAYOUT 2 — EDITORIAL PHOTO (foto full-bleed sopra)
 * ══════════════════════════════════════════════════════════════════════ */
function LayoutEditorial({ slug, displayName, color, city, bio, years, properties, services, photoUrl, agencyName, agencyLogoUrl }) {
  const ok = useImageOk(photoUrl);
  return (
    <Canvas bg="#FAF6EE" textColor="#1C1C1A">
      {/* TOP — Photo zone (730px) */}
      <div className="absolute" style={{ top: 0, left: 0, right: 0, height: 760, overflow: "hidden", background: "#1C1C1A" }}>
        {ok && photoUrl ? (
          <img src={photoUrl} alt="" crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover", filter: "saturate(0.85) contrast(1.05)" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Archivo','Inter',sans-serif", fontWeight: 900, fontSize: 360, color, opacity: 0.4 }}>
            {initialsOf(displayName)}
          </div>
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 35%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.85) 100%)" }} />
        {/* Top label */}
        <div className="absolute" style={{ top: 56, left: 64, right: 64, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ fontSize: 12, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, color: "rgba(255,255,255,0.85)" }}>
            Annuncio Personale · {new Date().getFullYear()}
          </div>
          <AgencyMark agencyName={agencyName} agencyLogoUrl={agencyLogoUrl} theme="dark" size={32} />
        </div>
        {/* Bottom-left big name on photo */}
        <div className="absolute" style={{ left: 64, right: 64, bottom: 48 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, color, marginBottom: 14 }}>
            Agente Immobiliare · {city}
          </div>
          <div style={{ fontFamily: "'Cormorant Garamond','Times New Roman',serif", fontWeight: 600, fontSize: 96, lineHeight: 0.94, letterSpacing: "-0.02em", color: "#FFFFFF" }}>
            {displayName}.
          </div>
        </div>
      </div>

      {/* BOTTOM — Light content area */}
      <div className="absolute" style={{ top: 760, left: 0, right: 0, bottom: 0, padding: "56px 64px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 40, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, color, marginBottom: 12 }}>
              ─ Chi sono
            </div>
            <div style={{ fontSize: 24, lineHeight: 1.36, fontWeight: 400, color: "#1C1C1A", letterSpacing: "-0.005em" }}>
              {bio}
            </div>
          </div>
          <div style={{ display: "flex", gap: 32, paddingTop: 6 }}>
            {[
              { v: properties, l: "Immobili" },
              { v: years || "·", l: years ? "Anni" : "Esperienza" },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 600, fontSize: 64, lineHeight: 1, color: "#1C1C1A" }}>{s.v}</div>
                <div style={{ fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 700, color: "rgba(28,28,26,0.55)", marginTop: 4 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div style={{ height: 1, background: "rgba(28,28,26,0.15)", marginBottom: 20 }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, color: "rgba(28,28,26,0.55)", marginBottom: 8 }}>Trovami qui</div>
              <div style={{ fontFamily: "'JetBrains Mono','SF Mono','Menlo',monospace", fontWeight: 700, fontSize: 30, color: "#1C1C1A", letterSpacing: "-0.015em", lineHeight: 1.05, wordBreak: "break-word" }}>
                <span style={{ color: "rgba(28,28,26,0.45)" }}>prontoimmo.it/agente/</span>
                <span style={{ color }}>{slug}</span>
              </div>
            </div>
            <div style={{ width: 78, height: 78, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 16px 36px ${color}55`, flexShrink: 0 }}>
              <svg width="34" height="34" viewBox="0 0 36 36" fill="none">
                <path d="M6 18 H 28 M 20 10 L 28 18 L 20 26" stroke="#0A0A0A" strokeWidth="3" strokeLinecap="square" strokeLinejoin="miter" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </Canvas>
  );
}

/* ══════════════════════════════════════════════════════════════════════
 * LAYOUT 3 — MINIMAL CREAM (light, serif, foto a destra)
 * ══════════════════════════════════════════════════════════════════════ */
function LayoutMinimal({ slug, displayName, color, city, bio, years, properties, services, photoUrl, agencyName, agencyLogoUrl }) {
  const [firstName, ...rest] = displayName.split(/\s+/);
  const lastName = rest.join(" ");
  const maxLen = Math.max(firstName.length, lastName.length || 0);
  const heroFontSize = maxLen > 12 ? 92 : maxLen > 10 ? 112 : maxLen > 8 ? 132 : 152;

  return (
    <Canvas bg="#FAF6EE" textColor="#1C1C1A">
      {/* Top bar */}
      <div className="absolute" style={{ top: 0, left: 0, right: 0, height: 6, background: color }} />
      <div className="absolute" style={{ top: 56, left: 80, right: 80, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.34em", textTransform: "uppercase", fontWeight: 700, color: "rgba(28,28,26,0.55)" }}>
          Annuncio Personale · {new Date().getFullYear()}
        </div>
        <AgencyMark agencyName={agencyName} agencyLogoUrl={agencyLogoUrl} theme="light" size={32} />
      </div>

      {/* HERO NAME serif */}
      <div className="absolute" style={{ top: 160, left: 80, right: 540 }}>
        <div style={{ fontSize: 12, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, color, marginBottom: 18 }}>
          Agente Immobiliare · {city}
        </div>
        <div style={{ fontFamily: "'Cormorant Garamond','Times New Roman',serif", fontWeight: 500, fontSize: heroFontSize, lineHeight: 0.92, letterSpacing: "-0.02em", color: "#1C1C1A" }}>
          {firstName}
          {lastName && (
            <>
              <br />
              <em style={{ color, fontStyle: "italic" }}>{lastName}.</em>
            </>
          )}
        </div>
      </div>

      {/* Photo right side */}
      <div className="absolute" style={{ top: 160, right: 80 }}>
        <AgentPhoto photoUrl={photoUrl} displayName={displayName} color={color} size={400} radius={2} accentBorder={false} />
        <div style={{ marginTop: 14, fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 600, color: "rgba(28,28,26,0.45)", textAlign: "right" }}>
          · 01 / Ritratto
        </div>
      </div>

      {/* Bio */}
      <div className="absolute" style={{ left: 80, right: 80, top: 760 }}>
        <div style={{ height: 1, background: "rgba(28,28,26,0.18)", marginBottom: 28 }} />
        <div style={{ display: "flex", gap: 48, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, color, marginBottom: 12 }}>
              ─ Chi sono
            </div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontStyle: "italic", fontSize: 30, lineHeight: 1.34, fontWeight: 400, color: "#1C1C1A", letterSpacing: "-0.005em" }}>
              «{bio}»
            </div>
          </div>
        </div>
      </div>

      {/* Stats inline */}
      <div className="absolute" style={{ left: 80, right: 80, top: 1020, display: "flex", gap: 56, borderTop: "1px solid rgba(28,28,26,0.18)", paddingTop: 28 }}>
        {[
          { v: properties, l: "Immobili in vetrina" },
          { v: years || "—", l: years ? "Anni di esperienza" : "Esperienza" },
          { v: city.toUpperCase(), l: "Zona di attività" },
        ].map((s, i) => (
          <div key={i} style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 600, fontSize: String(s.v).length > 6 ? 36 : 60, lineHeight: 1, color: "#1C1C1A", letterSpacing: "-0.02em" }}>
              {s.v}
            </div>
            <div style={{ fontSize: 10.5, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 700, color: "rgba(28,28,26,0.55)", marginTop: 8 }}>
              {s.l}
            </div>
          </div>
        ))}
      </div>

      {/* Bottom URL */}
      <div className="absolute" style={{ left: 80, right: 80, bottom: 56, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24 }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, color: "rgba(28,28,26,0.55)", marginBottom: 10 }}>Trovami qui</div>
          <div style={{ fontFamily: "'JetBrains Mono','SF Mono','Menlo',monospace", fontWeight: 700, fontSize: 30, color: "#1C1C1A", letterSpacing: "-0.015em", wordBreak: "break-word" }}>
            <span style={{ color: "rgba(28,28,26,0.45)" }}>prontoimmo.it/agente/</span>
            <span style={{ color }}>{slug}</span>
          </div>
        </div>
        <div style={{ width: 78, height: 78, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 16px 36px ${color}55`, flexShrink: 0 }}>
          <svg width="34" height="34" viewBox="0 0 36 36" fill="none">
            <path d="M6 18 H 28 M 20 10 L 28 18 L 20 26" stroke="#FAF6EE" strokeWidth="3" strokeLinecap="square" strokeLinejoin="miter" />
          </svg>
        </div>
      </div>
    </Canvas>
  );
}

/* ══════════════════════════════════════════════════════════════════════
 * LAYOUT 4 — MAGAZINE SPLIT (50/50 foto·testo + cornice)
 * ══════════════════════════════════════════════════════════════════════ */
function LayoutSplit({ slug, displayName, color, city, bio, years, properties, services, photoUrl, agencyName, agencyLogoUrl }) {
  const ok = useImageOk(photoUrl);
  const [firstName, ...rest] = displayName.split(/\s+/);
  const lastName = rest.join(" ");
  const maxLen = Math.max(firstName.length, lastName.length || 0);
  const heroFontSize = maxLen > 12 ? 54 : maxLen > 10 ? 64 : maxLen > 8 ? 78 : maxLen > 6 ? 92 : 108;

  return (
    <Canvas bg="#0E0E0C" textColor="#FAF6EE">
      {/* Outer frame */}
      <div className="absolute" style={{ inset: 32, border: `1px solid ${color}33`, pointerEvents: "none" }} />
      <div className="absolute" style={{ top: 32, bottom: 32, left: 540, width: 1, background: `${color}33` }} />

      {/* LEFT — Photo */}
      <div className="absolute" style={{ top: 32, bottom: 32, left: 32, width: 508, overflow: "hidden", background: "#1C1C1A" }}>
        {ok && photoUrl ? (
          <img src={photoUrl} alt="" crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover", filter: "saturate(0.92) contrast(1.04)" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Archivo','Inter',sans-serif", fontWeight: 900, fontSize: 240, color, opacity: 0.4 }}>
            {initialsOf(displayName)}
          </div>
        )}
        {/* Index ticker top-left */}
        <div className="absolute" style={{ top: 28, left: 28, fontSize: 11, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, color: "rgba(255,255,255,0.65)" }}>
          · 01 / Ritratto
        </div>
        {/* Vertical caption bottom-left */}
        <div className="absolute" style={{ bottom: 32, left: 28, right: 28, color: "#FFFFFF" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, color, marginBottom: 8, textShadow: "0 1px 6px rgba(0,0,0,0.6)" }}>
            Issue · {new Date().getFullYear()}
          </div>
        </div>
      </div>

      {/* RIGHT — Info column */}
      <div className="absolute" style={{ top: 32, bottom: 32, left: 540, right: 32, padding: "48px 56px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        {/* Top header */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 36 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.34em", textTransform: "uppercase", fontWeight: 700, color }}>
              Annuncio
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: "rgba(250,246,238,0.08)", border: "1px solid rgba(250,246,238,0.18)", color: "#FAF6EE", fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 600 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
              Online
            </div>
          </div>

          {/* HERO NAME */}
          <div style={{ fontFamily: "'Archivo','Inter',sans-serif", fontWeight: 900, fontSize: heroFontSize, lineHeight: 0.88, letterSpacing: "-0.045em", color: "#FAF6EE", textTransform: "uppercase" }}>
            {firstName}
            {lastName && (
              <>
                <br />
                <span style={{ color }}>{lastName}.</span>
              </>
            )}
          </div>

          {/* sub */}
          <div style={{ marginTop: 18, fontSize: 12, letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 600, color: "rgba(250,246,238,0.72)" }}>
            Agente Immobiliare · {city}
            {years && <> · {years} anni</>}
          </div>

          {/* Bio */}
          <div style={{ marginTop: 36, fontSize: 19, lineHeight: 1.42, fontWeight: 400, color: "rgba(250,246,238,0.86)", letterSpacing: "-0.005em" }}>
            {bio}
          </div>
        </div>

        {/* Stats compact */}
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 28 }}>
            {[
              { v: properties, l: "Immobili" },
              { v: years || "—", l: years ? "Anni" : "Esperienza" },
            ].map((s, i) => (
              <div key={i} style={{ background: "rgba(250,246,238,0.04)", border: "1px solid rgba(250,246,238,0.10)", borderRadius: 4, padding: "16px 18px" }}>
                <div style={{ fontFamily: "'Archivo','Inter',sans-serif", fontWeight: 900, fontSize: 40, lineHeight: 0.95, letterSpacing: "-0.02em", color, marginBottom: 6 }}>
                  {s.v}
                </div>
                <div style={{ fontSize: 9.5, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 600, color: "rgba(250,246,238,0.65)" }}>
                  {s.l}
                </div>
              </div>
            ))}
          </div>

          {/* divider */}
          <div style={{ height: 1, background: "rgba(250,246,238,0.15)", marginBottom: 20 }} />

          {/* URL */}
          <div style={{ fontSize: 10, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 700, color: "rgba(250,246,238,0.55)", marginBottom: 8 }}>
            Trovami qui
          </div>
          <div style={{ fontFamily: "'JetBrains Mono','SF Mono','Menlo',monospace", fontWeight: 700, fontSize: 22, color: "#FAF6EE", letterSpacing: "-0.015em", lineHeight: 1.15, wordBreak: "break-word", marginBottom: 24 }}>
            <span style={{ color: "rgba(250,246,238,0.5)" }}>prontoimmo.it/agente/</span><br />
            <span style={{ color }}>{slug}</span>
          </div>

          {/* Agency mark + arrow */}
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
            <AgencyMark agencyName={agencyName} agencyLogoUrl={agencyLogoUrl} theme="dark" size={28} />
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 16px 32px ${color}55`, flexShrink: 0 }}>
              <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
                <path d="M6 18 H 28 M 20 10 L 28 18 L 20 26" stroke="#0A0A0A" strokeWidth="3" strokeLinecap="square" strokeLinejoin="miter" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </Canvas>
  );
}
